/**
 * The order of the other people's tiles on the call stage, native and web.
 * The stages are components/VRSLayout.native.tsx and components/VRSLayout.tsx.
 *
 * Robert, 2026-10-07: "We should also be able to drag and drop anyone in an
 * odd-numbered position into the dominant video spot." Asked which spot that
 * is, he chose the simpler rule ("all tiles can switch with any other tile
 * just by dragging and dropping").
 *
 * - Only the tiles of the other people move. Your own video (floating, or a
 *   cell in Split) and a pane that waits for someone keep their places.
 * - A drop of one tile on another tile swaps the two.
 * - The order is for this call only. It is kept in memory by room name, and
 *   it is not stored and not sent to anyone. The next call starts in the
 *   usual order.
 * - A person who joins later goes after the tiles that the viewer arranged.
 *   When a person leaves, the tiles after theirs move up.
 *
 * Pure helpers here; the stages own the gesture.
 */
/** The usual order: nothing arranged. */
export const NO_TILE_ORDER = {
    keys: [],
    room: ''
};
/**
 * A press this long lifts a tile for a drag: on native, and for touch on the
 * web. A shorter press stays a tap, which shows or hides the toolbar.
 */
export const TILE_LIFT_PRESS_MS = 400;
/**
 * A finger that moves more than this many points before the press is long
 * enough does not lift the tile.
 */
export const TILE_PRESS_SLOP = 10;
/**
 * The arranged keys for this room. Another room gets the usual order.
 *
 * @param {ITileOrder} order - The viewer's arrangement.
 * @param {string} room - The room on the stage now.
 * @returns {string[]}
 */
export function tileOrderKeys(order, room) {
    return order.room === room ? order.keys : [];
}
/**
 * The keys of the tiles in the order to show. The tiles the viewer arranged
 * come first, in that order; tiles that are not in the arrangement follow in
 * the usual order.
 *
 * @param {string[]} keys - The tiles on the stage, in the usual order.
 * @param {string[]} arranged - The viewer's arrangement, or none.
 * @returns {string[]}
 */
export function arrangeTiles(keys, arranged) {
    if (!arranged.length) {
        return keys.slice();
    }
    const present = new Set(keys);
    const kept = arranged.filter(key => present.has(key));
    const placed = new Set(kept);
    return [...kept, ...keys.filter(key => !placed.has(key))];
}
/**
 * The panes in the order to show. Only the movable panes change places, and
 * only among the places that movable panes have in the usual order: your own
 * pane and a waiting pane stay where they are.
 *
 * @param {Array} items - The panes, in the usual order.
 * @param {Function} keyOf - The key of a pane.
 * @param {Function} movable - Whether a pane can move.
 * @param {string[]} arranged - The viewer's arrangement, or none.
 * @returns {Array}
 */
export function arrangePanes(items, keyOf, movable, arranged) {
    const movableItems = items.filter(movable);
    const byKey = new Map(movableItems.map(item => [keyOf(item), item]));
    // Two panes with one key cannot be told apart: keep the usual order.
    if (!arranged.length || movableItems.length < 2 || byKey.size !== movableItems.length) {
        return items.slice();
    }
    const order = arrangeTiles([...byKey.keys()], arranged);
    let next = 0;
    return items.map(item => {
        if (!movable(item)) {
            return item;
        }
        return byKey.get(order[next++]);
    });
}
/**
 * The keys after one tile is dropped on another: the two change places. A
 * key that is not shown, or a drop on the same tile, changes nothing.
 *
 * @param {string[]} keys - The movable tiles, in the order shown.
 * @param {string} from - The tile that moved.
 * @param {string} to - The tile it was dropped on.
 * @returns {string[]}
 */
export function swapTiles(keys, from, to) {
    const fromIndex = keys.indexOf(from);
    const toIndex = keys.indexOf(to);
    const next = keys.slice();
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return next;
    }
    next[fromIndex] = to;
    next[toIndex] = from;
    return next;
}
/**
 * The tile under a point, other than the tile that moves. A point on the
 * left or top edge of a tile is in it; a point on the right or bottom edge
 * is in the next one.
 *
 * @param {Map} rects - The place of each tile that can take a drop.
 * @param {Object} point - The point (the finger or the pointer).
 * @param {number} point.x - Across.
 * @param {number} point.y - Down.
 * @param {string} except - The tile that moves.
 * @returns {string|undefined}
 */
export function tileAtPoint(rects, point, except) {
    for (const [key, rect] of rects) {
        if (key !== except
            && point.x >= rect.x && point.x < rect.x + rect.width
            && point.y >= rect.y && point.y < rect.y + rect.height) {
            return key;
        }
    }
    return undefined;
}
